import React from "react";
import CodeBlock from "@/components/CodeBlock";

const RepositoryDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        CLI Command — add repository
      </h2>

      <p className="mb-6">
        The <strong>add repository</strong> command automatically generates a
        Repository Interface and its corresponding EF Core implementation under
        the Application and Infrastructure layers. Repositories provide a clean,
        persistent-agnostic abstraction for data access.
      </p>

      {/* BASIC USAGE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Basic Usage
        </h3>

        <CodeBlock
          language="bash"
          code={`axisnet add repository --name Product`}
        />

        <p className="mt-4">This will generate:</p>

        <CodeBlock
          language="bash"
          code={`Application/
  └── DataAccess/
      └── Repositories/
          └── IProductRepository.cs

Infrastructure/
  └── Persistence/
      └── Repositories/
          └── ProductRepository.cs`}
        />
      </div>

      {/* TEMPLATE — INTERFACE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Generated Interface Template
        </h3>

        <CodeBlock
          language="csharp"
          code={`using {{ProjectName}}.Domain;
using AxisNet.Infrastructure.Persistence.EntityFramework;

namespace {{ProjectName}}.Application.DataAccess.Repositories;

public interface I{{Domain}}Repository : IAsyncRepository<{{Domain}}, Guid>
{
    
}`}
        />
      </div>

      {/* TEMPLATE — IMPLEMENTATION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Generated Repository Implementation Template
        </h3>

        <CodeBlock
          language="csharp"
          code={`using {{ProjectName}}.Domain;
using AxisNet.Infrastructure.Persistence.EntityFramework;
using {{ProjectName}}.Application.DataAccess.Repositories;

namespace {{ProjectName}}.Infrastructure.Persistence.Repositories;

public class {{Domain}}Repository 
    : EfRepositoryBase<{{Domain}}, Guid, ApplicationDbContext>, I{{Domain}}Repository
{
    public {{Domain}}Repository(ApplicationDbContext context) 
        : base(context) {}
}`}
        />
      </div>

      {/* WITH DOMAIN OPTION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Generating Domain + Repository Together
        </h3>

        <p className="mb-4">
          If you want AxisNet CLI to generate the <strong>Domain entity</strong>{" "}
          along with the Repository files, use:
        </p>

        <CodeBlock
          language="bash"
          code={`axisnet add repository --name Product --domain`}
        />

        <p className="mt-4">This will additionally generate:</p>

        <CodeBlock
          language="bash"
          code={`Domain/
  └── Entities/
      └── Product.cs`}
        />
      </div>

      {/* RELATION WITH add feature */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Relationship with <code>add feature</code>
        </h3>

        <p className="mb-4">
          When using the <strong>add feature</strong> command, the{" "}
          <code>--repository</code> option automatically triggers repository
          generation.
        </p>

        <CodeBlock
          language="bash"
          code={`axisnet add feature --name Product --repository`}
        />

        <p className="mt-4">You can also chain domain generation:</p>

        <CodeBlock
          language="bash"
          code={`axisnet add feature --name Product --domain --repository`}
        />
      </div>

      {/* EFRepositoryBase Explanation */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          EfRepositoryBase Explained
        </h3>

        <p className="mb-4">
          Each repository inherits from <code>EfRepositoryBase</code>, which
          includes:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>CRUD Operations (Add, Update, Delete, Restore)</li>
          <li>Soft delete support</li>
          <li>
            Pagination (<code>Paginate&lt;T&gt;</code>)
          </li>
          <li>
            Filtering (<code>Filter</code>)
          </li>
          <li>Include / ThenInclude support</li>
        </ul>

        <p className="mt-4">
          This guarantees consistent query behavior across the entire
          application.
        </p>
      </div>

      {/* DI REGISTRATION */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Automatic Dependency Injection Registration
        </h3>

        <p className="mb-4">
          The CLI automatically adds the required DI registration inside:
        </p>

        <CodeBlock
          language="bash"
          code={`Infrastructure/DependencyInjection.cs`}
        />

        <p className="mb-4">Generated line example:</p>

        <CodeBlock
          language="csharp"
          code={`services.AddScoped<IProductRepository, ProductRepository>();`}
        />
      </div>

      {/* GIT AUTO SYNC */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12 mb-12">
        <h3 className="text-xl font-semibold text-emerald-400 mb-3">
          Git Auto-Sync Support
        </h3>

        <p className="mb-4">
          If Git automation flags are enabled, AxisNet CLI will automatically:
        </p>

        <ul className="list-disc pl-6 space-y-1">
          <li>stage new repository files</li>
          <li>commit with descriptive message</li>
          <li>push to the repository</li>
        </ul>

        <CodeBlock
          language="bash"
          code={`axisnet flags set git-sync on
axisnet flags set git-commit on
axisnet flags set git-push on`}
        />
      </div>
    </section>
  );
};

export default RepositoryDoc;
